import { useEffect, useState } from "react";
import PropTypes from "prop-types";
import classNames from "classnames";

import { numberSeparator, roundOff, contains } from "utils";
import { Filter, LineChart } from "components/Chart";
import { Title } from "style/components/Title.styles";
import { calcGainPercentage } from "features/Strategy/utils";
import { unixToDateTime, DATE_FORMAT } from "utils/date";
import { NoDataMessage } from "features/Strategy/v2/Detail/StrategyDetails.styles";
import { HistoricalPerformanceChartContainer } from "./HistoricalPerformanceChart.styles";
import { HistoricalPerformanceChartPlaceholder } from "./HistoricalPerformanceChartPlaceholder";
import { LegendContent as HpLegendContent } from "./Legend";
import { LegendContent } from "components/Chart/Legend";
import { TIME_INDICATOR } from "utils/constants";
import { COLOR_LIGHTER_GREY2 } from "style/theme";

const ChartTooltip = ({ active, payload, activePeriod }) => {
  if (active && payload && payload.length) {
    const period = activePeriod?.toLowerCase();
    let datetime = unixToDateTime(payload[0].payload.name, DATE_FORMAT);

    if (period === "1w" || period === "1m") {
      datetime = unixToDateTime(payload[0].payload.name);
    }
    return (
      <div className="chart-tooltip">
        <span className="hp-datetime">{datetime}</span>
      </div>
    );
  }

  return null;
};

ChartTooltip.propTypes = {
  active: PropTypes.bool,
  payload: PropTypes.array,
  activePeriod: PropTypes.string,
};

ChartTooltip.defaultProps = {
  active: false,
  payload: [],
};

const ChartTooltipWithProfit = ({ active, payload, firstValue }) => {
  if (active && payload && payload.length) {
    return (
      <div className="chart-tooltip">
        {!payload[0].payload.hideTimestamp && (
          <span className="datetime">
            {unixToDateTime(payload[0].payload.name, DATE_FORMAT)}
          </span>
        )}

        {payload.map((p, i) => {
          const value = p.value;
          const gain = p.value - firstValue;

          const gainPercentage = calcGainPercentage(firstValue, value);
          const formattedGainPercentage = gainPercentage
            ? numberSeparator(gainPercentage)?.replace("-", "")
            : gainPercentage;

          const formattedGain = gain
            ? numberSeparator(gain)?.replace("-", "")
            : gain;

          const isStrategyValue = contains(p.name, "Strategy");

          return (
            <div key={i} className="label" style={{ color: p.color }}>
              {/* Label */}
              <span
                className={classNames({
                  gain: isStrategyValue && value >= firstValue,
                  loss: isStrategyValue && value < firstValue,
                })}
              >
                {p.payload.label || p.name}{" "}
              </span>
              {/* Value */}
              <span
                className={classNames({
                  gain: isStrategyValue && value >= firstValue,
                  loss: isStrategyValue && value < firstValue,
                })}
              >
                {value >= firstValue ? "+" : "-"}${formattedGain} (
                {value >= firstValue ? "+" : "-"}
                {formattedGainPercentage}%)
              </span>
            </div>
          );
        })}
      </div>
    );
  }

  return null;
};

ChartTooltipWithProfit.propTypes = {
  active: PropTypes.bool,
  payload: PropTypes.array,
  firstValue: PropTypes.number,
};

ChartTooltipWithProfit.defaultProps = {
  active: false,
  payload: [],
  firstValue: 0,
};

export const HistoricalPerformanceChart = (props) => {
  const {
    height,
    lines,
    showHeading,
    timeframe,
    data,
    nodata,
    isLoading,
    handleFilterClick,
    activePeriod,
    legendContent,
    showLegend,
    showTooltipDate,
    showTopTooltipProfit,
  } = props;

  const [gain, setGain] = useState(0);
  const [benchmark, setBenchmark] = useState(null);
  const [activeValue, setActiveValue] = useState(0);
  const [gainPercentage, setGainPercentage] = useState(0);
  const [benchmarkPerc, setBenchmarkPerc] = useState(0);
  const [isTooltipActive, setIsTooltipActive] = useState(false);

  const dataLen = data.length;
  const stratKey = "Strategy Value";
  const bmKey = "Benchmark Value (SPY)";
  const firstValue = dataLen && data[0][stratKey];
  const firstBMValue = dataLen && data[0][bmKey];

  useEffect(() => {
    if (!dataLen || isTooltipActive) {
      return;
    }
    const lastValue = data[dataLen - 1][stratKey];
    const lastBMValue = data[dataLen - 1][bmKey];
    handleCalcProfitLoss(lastValue, { value: lastBMValue });
  }, [data, isTooltipActive]);

  const onMouseMove = (e) => {
    if (!e) return;
    const { activePayload } = e;

    if (activePayload && activePayload.length > 0) {
      const stratValue = activePayload[0].value;
      const bmObject = activePayload[1];
      handleCalcProfitLoss(stratValue, bmObject);
    }
  };

  const handleCalcProfitLoss = (stratVal, bmObj) => {
    const calcGain = stratVal - firstValue;
    setActiveValue(stratVal);
    setGain(calcGain);

    if (firstValue !== 0) {
      const gainPerc = calcGainPercentage(firstValue, stratVal);
      setGainPercentage(roundOff(gainPerc));

      // Benchmark
      if (bmObj && bmObj.value) {
        const calcBm = bmObj.value - firstValue;
        const percentage = calcGainPercentage(firstValue, bmObj.value);
        setBenchmark({
          value: bmObj.value,
          color: bmObj.color,
          gain: calcBm,
          percentage,
        });
        const bmPerc = calcGainPercentage(firstBMValue, bmObj.value);
        if (bmPerc) setBenchmarkPerc(roundOff(bmPerc));
      }
    }
  };

  const formattedGain = () => {
    if (gain) {
      return numberSeparator(gain)?.replace("-", "");
    }
    return gain;
  };

  const formattedGainPerc = () => {
    if (gainPercentage) {
      return numberSeparator(gainPercentage)?.replace("-", "");
    }
    return gainPercentage;
  };

  const formattedBenchmark = () => {
    if (benchmark?.gain) {
      return numberSeparator(benchmark?.gain)?.replace("-", "");
    }
    return benchmark?.gain;
  };

  const formattedBenchmarkPerc = () => {
    if (benchmark?.percentage) {
      return numberSeparator(benchmark?.percentage)?.replace("-", "");
    }
    return benchmark?.percentage;
  };

  const getLegendContent = () => {
    if (!showLegend) {
      return;
    }

    if (props.tooltipContent) {
      return {
        ...legendContent,
        content: LegendContent,
        wrapperStyle: { marginTop: "15px" },
      };
    }

    return {
      ...legendContent,
      content: (props) => (
        <HpLegendContent
          gainPercentage={gainPercentage}
          gain={gain}
          benchmarkPerc={benchmarkPerc}
          benchmark={benchmark}
          activeValue={activeValue}
          firstValue={firstValue}
          isTooltipActive={isTooltipActive}
          bmKey={bmKey}
          stratKey={stratKey}
          activePeriod={activePeriod}
          {...props}
        />
      ),
      wrapperStyle: { marginTop: "15px" },
    };
  };

  const getTooltipContent = (data) => {
    setIsTooltipActive(data.active);
    if (showTooltipDate) {
      return <ChartTooltip {...data} activePeriod={activePeriod} />;
    }

    return <ChartTooltipWithProfit {...data} firstValue={firstValue} />;
  };

  if (isLoading) {
    return <HistoricalPerformanceChartPlaceholder {...props} />;
  }

  return (
    <HistoricalPerformanceChartContainer>
      {showHeading && (
        <Title className="line-chart-heading">Historical Performance</Title>
      )}

      {dataLen > 0 && (
        <>
          {/* Chart Hover Value */}
          {!props.tooltipContent && (
            <div className="investment-value">
              <div className="total">
                <p className="amount">${numberSeparator(activeValue)} </p>
                {showTopTooltipProfit && (
                  <>
                    <p className="extra-info">
                      <span
                        className={classNames({
                          gain: activeValue >= firstValue,
                          loss: activeValue < firstValue,
                        })}
                      >
                        <span>{stratKey} </span>
                        {activeValue >= firstValue ? "+" : "-"}$
                        {formattedGain()} (
                        {activeValue >= firstValue ? "+" : "-"}
                        {formattedGainPerc()}%)
                      </span>
                      <span className="date">
                        {TIME_INDICATOR[activePeriod?.toUpperCase()]}
                      </span>
                    </p>
                    {benchmark && (
                      <p className="extra-info">
                        <span
                          style={{
                            color: benchmark?.color || COLOR_LIGHTER_GREY2,
                          }}
                        >
                          <span>{bmKey} </span>
                          {benchmark?.value >= firstBMValue ? "+" : "-"}$
                          {formattedBenchmark()} (
                          {benchmark?.value >= firstBMValue ? "+" : "-"}
                          {formattedBenchmarkPerc()}%)
                        </span>
                      </p>
                    )}
                  </>
                )}
              </div>
            </div>
          )}

          {/* Chart */}
          <div style={{ width: "100%", height }} className="chart-wrapper">
            <LineChart
              data={data}
              lines={lines}
              lineChartProps={{ onMouseMove }}
              tooltipProps={{ position: { x: "auto" } }}
              yAxisProps={{
                hide: true,
                domain: [10000, "dataMax"],
              }}
              tooltipContent={getTooltipContent}
              {...props}
              legendContent={getLegendContent()}
            />
          </div>
        </>
      )}

      {!dataLen && (
        <div style={{ width: "100%", height }} className="chart-wrapper">
          <NoDataMessage className="no-data">{nodata}</NoDataMessage>
        </div>
      )}

      {timeframe && (
        <Filter
          timeframe={timeframe}
          activePeriod={activePeriod}
          handleFilterClick={handleFilterClick}
          className="h-performance-filter"
        />
      )}
    </HistoricalPerformanceChartContainer>
  );
};

HistoricalPerformanceChart.propTypes = {
  height: PropTypes.string,
  data: PropTypes.array,
  lines: PropTypes.array,
  showHeading: PropTypes.bool,
  nodata: PropTypes.string,
  isLoading: PropTypes.bool,
  timeframe: PropTypes.array,
  handleFilterClick: PropTypes.func,
  activePeriod: PropTypes.string,
  tooltipContent: PropTypes.any,
  legendContent: PropTypes.any,
  showLegend: PropTypes.bool,
  showTooltipDate: PropTypes.bool,
  showTopTooltipProfit: PropTypes.bool,
};

HistoricalPerformanceChart.defaultProps = {
  data: [],
  height: "350px",
  nodata: "No historical data found",
  showTopTooltipProfit: true,
  showTooltipDate: false,
  showLegend: true,
};
